import {
  CHART_ANALYTICS_DEFINITIONS,
  type ChartAnalyticsDefinition,
} from '@/features/chart/lib/config/chart-analytics'
import { LEVEL_PROFILE_STUDIES } from '@/features/chart/lib/level-profile-studies'
import {
  ORDERBOOK_HEATMAP_INDICATOR_ID,
  ORDERBOOK_HEATMAP_STUDY,
} from '@/features/chart/lib/orderbook-heatmap'
import type { PickerIndicatorItem } from '@/features/chart/lib/percept-indicator-utils'
import { VISIBLE_RANGE_PROFILE_STUDIES } from '@/features/chart/lib/visible-range-profile-studies'
import type { LevelProfileId, VisibleRangeProfileId } from '@/types/dashboard'

export interface ChartBuiltinIndicatorDefinition {
  id: string
  nameKey: string
  descriptionKey: string
  shortCode: string
  placement: 'overlay' | 'pane'
  tvName: string
  isOverlay: boolean
}

export const CHART_BUILTIN_INDICATORS = [
  {
    id: 'tv-sma',
    nameKey: 'builtInIndicators.tv-sma.name',
    descriptionKey: 'builtInIndicators.tv-sma.description',
    shortCode: 'SMA',
    placement: 'overlay',
    tvName: 'Moving Average',
    isOverlay: true,
  },
  {
    id: 'tv-ema',
    nameKey: 'builtInIndicators.tv-ema.name',
    descriptionKey: 'builtInIndicators.tv-ema.description',
    shortCode: 'EMA',
    placement: 'overlay',
    tvName: 'Moving Average Exponential',
    isOverlay: true,
  },
  {
    id: 'tv-rsi',
    nameKey: 'builtInIndicators.tv-rsi.name',
    descriptionKey: 'builtInIndicators.tv-rsi.description',
    shortCode: 'RSI',
    placement: 'pane',
    tvName: 'Relative Strength Index',
    isOverlay: false,
  },
  {
    id: 'tv-bollinger',
    nameKey: 'builtInIndicators.tv-bollinger.name',
    descriptionKey: 'builtInIndicators.tv-bollinger.description',
    shortCode: 'BB',
    placement: 'overlay',
    tvName: 'Bollinger Bands',
    isOverlay: true,
  },
  {
    id: 'tv-macd',
    nameKey: 'builtInIndicators.tv-macd.name',
    descriptionKey: 'builtInIndicators.tv-macd.description',
    shortCode: 'MACD',
    placement: 'pane',
    tvName: 'MACD',
    isOverlay: false,
  },
  {
    id: 'tv-obv',
    nameKey: 'builtInIndicators.tv-obv.name',
    descriptionKey: 'builtInIndicators.tv-obv.description',
    shortCode: 'OBV',
    placement: 'pane',
    tvName: 'On Balance Volume',
    isOverlay: false,
  },
  {
    id: 'tv-volume',
    nameKey: 'builtInIndicators.tv-volume.name',
    descriptionKey: 'builtInIndicators.tv-volume.description',
    shortCode: 'VOL',
    placement: 'pane',
    tvName: 'Volume',
    isOverlay: false,
  },
  {
    id: 'tv-stochastic',
    nameKey: 'builtInIndicators.tv-stochastic.name',
    descriptionKey: 'builtInIndicators.tv-stochastic.description',
    shortCode: 'STOCH',
    placement: 'pane',
    tvName: 'Stochastic',
    isOverlay: false,
  },
  {
    id: 'tv-adx',
    nameKey: 'builtInIndicators.tv-adx.name',
    descriptionKey: 'builtInIndicators.tv-adx.description',
    shortCode: 'ADX',
    placement: 'pane',
    tvName: 'Average Directional Index',
    isOverlay: false,
  },
  {
    id: 'tv-cci',
    nameKey: 'builtInIndicators.tv-cci.name',
    descriptionKey: 'builtInIndicators.tv-cci.description',
    shortCode: 'CCI',
    placement: 'pane',
    tvName: 'Commodity Channel Index',
    isOverlay: false,
  },
  {
    id: 'tv-ichimoku',
    nameKey: 'builtInIndicators.tv-ichimoku.name',
    descriptionKey: 'builtInIndicators.tv-ichimoku.description',
    shortCode: 'ICHI',
    placement: 'overlay',
    tvName: 'Ichimoku Cloud',
    isOverlay: true,
  },
] as const satisfies readonly ChartBuiltinIndicatorDefinition[]

export const CHART_BUILTIN_STUDY_NAMES = Object.fromEntries(
  CHART_BUILTIN_INDICATORS.map(({ id, tvName, isOverlay }) => [id, { tvName, isOverlay }])
) as Record<string, { tvName: string; isOverlay: boolean }>

export const PICKER_CHART_ANALYTICS_DEFINITIONS: readonly ChartAnalyticsDefinition[] =
  CHART_ANALYTICS_DEFINITIONS.filter((definition) => definition.discoverable)

export const CHART_PROFILE_INDICATORS = [
  {
    id: 'volumeDeltaProfile' satisfies VisibleRangeProfileId,
    name: 'Volume Delta Profile',
    description: 'Buy and sell volume bucketed by price over the visible chart range.',
    shortCode: 'VDP',
    placement: 'overlay',
    tvName: VISIBLE_RANGE_PROFILE_STUDIES.volume.name,
  },
  {
    id: 'liquidationProfile' satisfies VisibleRangeProfileId,
    name: 'Liquidation Profile',
    description: 'Buy and sell liquidations bucketed by price over the visible chart range.',
    shortCode: 'LP',
    placement: 'overlay',
    tvName: VISIBLE_RANGE_PROFILE_STUDIES.liquidation.name,
  },
  {
    id: 'openInterestProfile' satisfies VisibleRangeProfileId,
    name: 'Open Interest Profile',
    description:
      'Open-interest increases and decreases bucketed by price over the visible chart range.',
    shortCode: 'OIP',
    placement: 'overlay',
    tvName: VISIBLE_RANGE_PROFILE_STUDIES.openInterest.name,
  },
] as const satisfies readonly (PickerIndicatorItem & { tvName: string })[]

// Hyperliquid liquidation & stop-level profiles (oems #261) — kept separate from
// CHART_PROFILE_INDICATORS/VisibleRangeProfileId: a structurally different data pipeline (live
// AnalyticsStream, not a one-shot visible-range HTTP request) even though it shares the same
// picker/study-registration pattern.
export const LEVEL_PROFILE_INDICATORS = [
  {
    id: 'liquidationLevelProfile' satisfies LevelProfileId,
    name: 'Liquidation Levels',
    description: 'Predicted liquidation prices of currently-open positions, bucketed by price.',
    shortCode: 'LLP',
    placement: 'overlay',
    tvName: LEVEL_PROFILE_STUDIES.liquidationLevels.name,
  },
  {
    id: 'stopLevelProfile' satisfies LevelProfileId,
    name: 'Stop Levels',
    description: 'Resting take-profit and stop-loss trigger prices, bucketed by price.',
    shortCode: 'SLP',
    placement: 'overlay',
    tvName: LEVEL_PROFILE_STUDIES.stopLevels.name,
  },
] as const satisfies readonly (PickerIndicatorItem & { tvName: string })[]

export const ORDERBOOK_HEATMAP_INDICATOR = {
  id: ORDERBOOK_HEATMAP_INDICATOR_ID,
  name: 'Order Book Heatmap',
  description:
    'Resting bid and ask size within ±25% of mid, one snapshot per bar, shaded behind the candles.',
  shortCode: 'OBH',
  placement: 'overlay',
  tvName: ORDERBOOK_HEATMAP_STUDY.name,
} as const satisfies PickerIndicatorItem & { tvName: string }

export const SPECIAL_ANALYTICS_STUDY_NAMES = {
  buySellLiq: 'OrderX Liquidation Split',
  netBuyTradeCount: 'OrderX Trade Count (Buy/Sell)',
} as const

function getAnalyticsStudyName(definition: ChartAnalyticsDefinition) {
  if (definition.key === 'buySellLiq') return SPECIAL_ANALYTICS_STUDY_NAMES.buySellLiq
  if (definition.key === 'netBuyTradeCount') {
    return SPECIAL_ANALYTICS_STUDY_NAMES.netBuyTradeCount
  }
  return `OrderX ${definition.label}`
}

export const NATIVE_INDICATOR_STUDY_NAMES = [
  ...CHART_BUILTIN_INDICATORS.map((indicator) => indicator.tvName),
  ...PICKER_CHART_ANALYTICS_DEFINITIONS.map(getAnalyticsStudyName),
  ...CHART_PROFILE_INDICATORS.map((indicator) => indicator.tvName),
  ...LEVEL_PROFILE_INDICATORS.map((indicator) => indicator.tvName),
  ORDERBOOK_HEATMAP_INDICATOR.tvName,
] as const

// Compare and Overlay are not picker items, but the native Compare button uses
// these internal studies and must remain allowed by the whitelist.
export const NATIVE_STUDIES_ACCESS_NAMES = [
  ...NATIVE_INDICATOR_STUDY_NAMES,
  'Compare',
  'Overlay',
] as const
